import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import EntitiesModal from './EntitiesModal';
import { LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { List } from 'react-window';

const JOB_ROW_HEIGHT = 53;
const JOBS_TABLE_HEIGHT = 300;
const GRID_COLUMNS = '1fr 1fr 1fr 1fr 1fr 120px 1fr 1fr 1fr';


export const dbs = {
    "STG_VIRGINIA": "RPA STG",
    "STG_RPDB2": "RPNA STG",
    "PROD_VIRGINIA_RPA": "RPA PROD",
    "PROD_VIRGINIA_RPNA": "RPNA PROD",
    "PROD_IRELAND_RPA_FROM_VIRGINIA": "RPA PROD REPLICA",
    "PROD_IRELAND_RPNA_FROM_VIRGINIA": "RPNA PROD REPLICA",
    "EDGE_STG": "EDGE STG",
    "EDGE_PROD": "EDGE PROD",
    "V2": "EDGE DEV",
}

export const getTargetName = (db) => {
    return (dbs[db] || db)
}

export const getStatusColor = (status) => {
    const statusColor = {
        "FAILED": "#FF0000",
        "FINISHED": "#52eb34",
        "PROCESSING": "#e39127",
        "MERGING": "#e39127",
        "default": "#27e3dd",
    }
    return (statusColor[status] || statusColor["default"])
}

export default function Row({row, fetchJobIds, theme}) {
    const [open, setOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const muiTheme = useTheme();

    const JobRow = ({ index, style, history: jobs, theme }) => {
        const job = jobs[index];
        if (!job) return null;
        return (
          <Box component="div" style={style} sx={{ width: '100%', boxSizing: 'border-box' }}>
            <Box
              component="div"
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_COLUMNS,
                gap: 0,
                alignItems: 'center',
                borderBottom: 1,
                borderColor: 'divider',
                px: 1,
                py: 0.5,
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.syncJobId}</Box>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.zipId}</Box>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.syncType === "METADATA" ? "EXTERNAL DATA" : job.syncType}</Box>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{getTargetName(job.source)}</Box>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.description}</Box>
              <Box component="span">{job.syncType === "METADATA" || !job.zipId ? "N/A" : <EntitiesModal zipId={job.zipId} theme={theme} />}</Box>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.appliedAt?.substring(0, 10)} {job.appliedAt?.substring(11)}</Box>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.finishedAt?.substring(0, 10)} {job.finishedAt?.substring(11)}</Box>
              <Box component="span" sx={{ color: getStatusColor(job.status) }}>{job.status}</Box>
            </Box>
          </Box>
        );
      };

    const handleRowClick = async () => {
        setOpen(!open);
        if (!open && history.length === 0) {
            await fetchJobIds(row.syncBatchId).then((jobDetails) => {
                setHistory(jobDetails)
            });
        }
    };

    return (
        <React.Fragment>
            <TableRow sx={{'& > *': {borderBottom: 'unset'}}}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={handleRowClick}>
                        {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {row.syncBatchId}
                </TableCell>
                <TableCell align="right">{row.login}</TableCell>
                <TableCell align="right">{getTargetName(row.syncTarget)}</TableCell>
                <TableCell align="right">{row.syncType === "METADATA" ? "EXTERNAL DATA" : row.syncType}</TableCell>
                <TableCell align="right">{row.syncEntityCount}</TableCell>
                <TableCell align="right">{row.syncMetadataCount}</TableCell>
                <TableCell align="right">{row.syncStart?.substring(0, 10)} {row.syncStart?.substring(11)}</TableCell>
                <TableCell align="right">{row.syncFinishTime?.substring(0, 10)} {row.syncFinishTime?.substring(11)}</TableCell>
                <TableCell align="right" style={{color: `${getStatusColor(row.syncStatus)}`}}>{row.syncStatus}</TableCell>
            </TableRow>
            {history.length <= 0 && open ? <LinearProgress sx={{ position: 'absolute', width: '100%' }} /> : 
                (
                    <TableRow>
                        <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={10}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                                <Box sx={{margin: 1}}>
                                    <Typography variant="h6" gutterBottom component="div">
                                        Sync Job Details
                                    </Typography>
                                    <Box sx={{ width: '100%', overflow: 'hidden' }} aria-label="jobs">
                                        <Box
                                            component="div"
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: GRID_COLUMNS,
                                                gap: 0,
                                                alignItems: 'center',
                                                px: 1,
                                                py: 0.5,
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                                fontWeight: 600,
                                                backgroundColor: 'action.hover',
                                            }}
                                        >
                                            <Box component="span">Job ID</Box>
                                            <Box component="span">Zip ID</Box>
                                            <Box component="span">Zip Content</Box>
                                            <Box component="span">Source</Box>
                                            <Box component="span">Description</Box>
                                            <Box component="span">Entities</Box>
                                            <Box component="span">Applied At</Box>
                                            <Box component="span">Finished At</Box>
                                            <Box component="span">Merge status</Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                '& > div': {
                                                    scrollbarWidth: 'thin',
                                                    scrollbarColor: `${muiTheme.palette.divider} ${muiTheme.palette.background.default}`,
                                                    '&::-webkit-scrollbar': {
                                                        width: 8,
                                                        height: 8,
                                                    },
                                                    '&::-webkit-scrollbar-track': {
                                                        background: muiTheme.palette.background.default,
                                                    },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        backgroundColor: muiTheme.palette.divider,
                                                        borderRadius: 4,
                                                    },
                                                    '&::-webkit-scrollbar-thumb:hover': {
                                                        backgroundColor: muiTheme.palette.action.hover,
                                                    },
                                                },
                                            }}
                                        >
                                            <List
                                                rowComponent={JobRow}
                                                rowCount={history.length}
                                                rowHeight={JOB_ROW_HEIGHT}
                                                rowProps={{ history, theme }}
                                                style={{
                                                    height: JOBS_TABLE_HEIGHT,
                                                    width: '100%',
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Collapse>
                        </TableCell>
                    </TableRow>
                )}
        </React.Fragment>
    );
}